import { ActionIcon, CloseButton, Text, TextInput } from "@mantine/core";
import { ArrowLeft, ChevronLeft, ChevronRight, Search, type LucideIcon } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import classes from "./transitions.module.css";

export interface TileCatalogProps<T, C extends string> {
  /** What the tiles are, in the tab's words: "transitions", "effects". */
  noun: string;
  /** Live's rows, in its order. */
  categories: readonly { id: C; label: string }[];
  /** Every tile, row by row, in live's order. */
  items: readonly T[];
  categoryOf: (item: T) => C;
  keyOf: (item: T) => string;
  /** Live's search over a list. */
  search: (list: readonly T[], query: string) => readonly T[];
  renderTile: (item: T) => ReactNode;
  /** The icon over an empty search's words. */
  NoneIcon: LucideIcon;
}

/**
 * Live's tile tabs (Transitions #240, Effects #265): "Search {noun}...", then a row per category
 * with "View all", or one category's or the search's tiles in a grid with "{n} {noun}"; an empty
 * search says "No {noun} found".
 */
export function TileCatalog<T, C extends string>(props: TileCatalogProps<T, C>) {
  const { noun, categories, items, categoryOf, keyOf, renderTile, NoneIcon } = props;
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<C | null>(null);
  const searching = query.trim() !== "";
  const open = categories.find((each) => each.id === category);
  const listed = props.search(
    category === null ? items : items.filter((each) => categoryOf(each) === category),
    query,
  );
  const found = `${noun.charAt(0).toUpperCase()}${noun.slice(1)} found`;
  return (
    <div className={classes.tab}>
      <div className={classes.top}>
        <TextInput
          classNames={{ input: classes.searchInput }}
          placeholder={`Search ${noun}...`}
          aria-label={`Search ${noun}`}
          leftSection={<Search size={14} aria-hidden />}
          rightSection={
            searching ? (
              <CloseButton size="sm" aria-label="Clear the search" onClick={() => setQuery("")} />
            ) : null
          }
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
        {open !== undefined && !searching ? (
          <div className={classes.categoryHead}>
            <button type="button" className={classes.back} onClick={() => setCategory(null)}>
              <span className={classes.backIcon}>
                <ArrowLeft size={14} aria-hidden />
              </span>
              {open.label}
            </button>
            <Text className={classes.count}>
              {listed.length} {noun}
            </Text>
          </div>
        ) : null}
      </div>
      {category === null && !searching ? (
        <div className={classes.rows}>
          {categories.map((each) => (
            <CategoryRow
              key={each.id}
              label={each.label}
              items={items.filter((item) => categoryOf(item) === each.id)}
              keyOf={keyOf}
              renderTile={renderTile}
              onViewAll={() => setCategory(each.id)}
            />
          ))}
        </div>
      ) : listed.length === 0 ? (
        <div className={classes.none}>
          <NoneIcon size={32} aria-hidden className={classes.noneIcon} />
          <Text className={classes.noneTitle}>No {noun} found</Text>
          <Text className={classes.noneHint}>Try searching for a different keyword or category</Text>
        </div>
      ) : (
        <ul className={classes.grid} aria-label={open?.label ?? found}>
          {listed.map((item) => (
            <li key={keyOf(item)}>{renderTile(item)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One category of live's overview: its title and "View all", over a row that scrolls across. */
function CategoryRow<T>(props: {
  label: string;
  items: readonly T[];
  keyOf: (item: T) => string;
  renderTile: (item: T) => ReactNode;
  onViewAll: () => void;
}) {
  const row = useRef<HTMLUListElement>(null);
  const [ends, setEnds] = useState({ left: false, right: false });
  useEffect(() => {
    const element = row.current;
    if (element === null) return;
    const check = () =>
      setEnds({
        left: element.scrollLeft > 5,
        right: element.scrollWidth - element.scrollLeft - element.clientWidth > 5,
      });
    // Live's row turns the wheel's up and down into across, until it reaches an end.
    const wheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const atStart = element.scrollLeft <= 0 && event.deltaY < 0;
      const atEnd = element.scrollLeft >= element.scrollWidth - element.clientWidth - 1 && event.deltaY > 0;
      if (element.scrollWidth <= element.clientWidth || atStart || atEnd) return;
      element.scrollLeft += event.deltaY;
      event.preventDefault();
    };
    check();
    element.addEventListener("scroll", check, { passive: true });
    element.addEventListener("wheel", wheel, { passive: false });
    window.addEventListener("resize", check);
    return () => {
      element.removeEventListener("scroll", check);
      element.removeEventListener("wheel", wheel);
      window.removeEventListener("resize", check);
    };
  }, []);
  const scroll = (by: number) => row.current?.scrollBy({ left: by, behavior: "smooth" });
  return (
    <section className={classes.row} aria-label={props.label}>
      <div className={classes.rowHead}>
        <button type="button" className={classes.rowTitle} onClick={props.onViewAll}>
          {props.label}
        </button>
        <button
          type="button"
          className={classes.viewAll}
          aria-label={`View all ${props.label}`}
          onClick={props.onViewAll}
        >
          View all
          <ChevronRight size={14} aria-hidden />
        </button>
      </div>
      <div className={classes.rowBody}>
        {ends.left ? (
          <ActionIcon
            className={classes.scroll}
            data-side="left"
            radius="xl"
            size={24}
            aria-label="Scroll left"
            onClick={() => scroll(-180)}
          >
            <ChevronLeft size={14} />
          </ActionIcon>
        ) : null}
        <ul ref={row} className={classes.strip}>
          {props.items.map((item) => (
            <li key={props.keyOf(item)} className={classes.stripItem}>
              {props.renderTile(item)}
            </li>
          ))}
        </ul>
        {ends.right ? (
          <ActionIcon
            className={classes.scroll}
            data-side="right"
            radius="xl"
            size={24}
            aria-label="Scroll right"
            onClick={() => scroll(180)}
          >
            <ChevronRight size={14} />
          </ActionIcon>
        ) : null}
      </div>
    </section>
  );
}
